import Component from 'flarum/common/Component';

/**
 * Reusable accordion section used by the taro-case modules.
 *
 * Uses sessionStorage to remember collapsed/expanded state per section key
 * across page loads. Defaults to expanded on first visit.
 */
export default class CollapsibleSection extends Component {
  oninit(vnode) {
    // CRITICAL: must call super.oninit() in Flarum. The base class's
    // oninit runs `this.setAttrs(vnode.attrs)`, which populates
    // `this.attrs`. Without it, `this.attrs` is undefined when
    // `view()` later destructures it — leading to
    // `TypeError: Cannot read properties of undefined (reading 'title')`.
    super.oninit(vnode);

    this.key = vnode.attrs.key || vnode.attrs.title;
    this.storageKey = 'taro-case.collapse.' + this.key;
    this.state = this.readState();
  }

  readState() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (raw === 'collapsed' || raw === 'expanded') return raw;
    } catch (e) {
      // ignore (private mode etc.)
    }
    return 'expanded';
  }

  writeState(value) {
    this.state = value;
    try {
      sessionStorage.setItem(this.storageKey, value);
    } catch (e) {
      // ignore
    }
  }

  toggle() {
    this.writeState(this.state === 'expanded' ? 'collapsed' : 'expanded');
  }

  view(vnode) {
    // Safe to use `this.attrs` here: `view()` is called AFTER `oninit`, and
    // any further redraw will have populated `this.attrs` via the parent's
    // re-render cycle. (No risk of undefined because we don't read
    // `this.attrs` inside our own `oninit`.)
    const { title, badge, icon } = this.attrs;
    // 使用 vnode.children 而不是 this.attrs.children 来解决 SSR 水合问题：
    // SSR 过程会固化 this.attrs.children 为 null（因为 SSR 时 user=null
    // 导致 render* 方法返回空），客户端水合后 this.attrs.children 不会
    // 被更新。vnode.children 是 Mithril 每次渲染时从父组件 vnode tree
    // 实时获取的，能正确反映客户端最新的子节点内容。
    const children = vnode.children;
    const isOpen = this.state === 'expanded';
    const headerId = `taro-collapse-h-${this.key}`;
    const panelId = `taro-collapse-p-${this.key}`;

    return (
      <section className={`TaroCollapse TaroCollapse--${this.key} ${isOpen ? 'TaroCollapse--open' : 'TaroCollapse--closed'}`}>
        <h2
          className="TaroCollapse-header"
          id={headerId}
          onclick={() => this.toggle()}
          role="button"
          tabindex="0"
        >
          <span className="TaroCollapse-icon">
            {icon || <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} />}
          </span>
          <span className="TaroCollapse-title">{title}</span>
          {badge ? <span className="TaroCollapse-badge">{badge}</span> : null}
        </h2>
        {isOpen ? (
          <div className="TaroCollapse-panel" id={panelId} role="region" aria-labelledby={headerId}>
            {children}
          </div>
        ) : null}
      </section>
    );
  }
}
